import { produce } from 'immer';
import { flushSync } from 'react-dom';
import type {
  Page,
  PageSize,
  PageBackground,
  Element,
  GroupElement,
  ImageElement,
  TableElement,
  TableCell,
  TextElement,
  LineElement,
  RectangleElement,
  CircleElement,
  TriangleElement,
  VectorElement,
  VectorShape,
  ChartElement,
  ChartType,
  ElementAnimation,
  ElementAppearance,
  ElementInteraction,
  ElementLayout,
  ElementMeta,
  ElementPatch,
  ElementSize,
  ElementDataMap,
  ElementType,
  PageGuideSettings,
  GridGuideSettings,
  ParagraphStyle,
  TextContentData,
  TypographyStyle,
} from '@/components/canvas/document/publicTypes';
import {
  DEFAULT_GRID_GUIDE_SETTINGS,
  DEFAULT_PAGE_GUIDE_SETTINGS,
  clampGridGuideValue,
  GRID_COLUMNS,
  GRID_GUTTER,
  GRID_MARGIN,
  GRID_ROWS,
  resolvePageGuideSettings,
} from '@/components/canvas/page/guideSettings';
import { DEFAULT_DESIGNER_FONT } from '@/components/canvas/shared/constants/fonts';
import {
  TABLE_BORDER_COLOR,
  TABLE_BORDER_WIDTH,
  TABLE_BORDER_STYLE,
  TABLE_DEFAULT_FONT_SIZE,
  TABLE_DEFAULT_FONT_WEIGHT,
  TABLE_DEFAULT_TEXT_ALIGN,
  TABLE_DEFAULT_TEXT_COLOR,
} from '@/components/canvas/shared/constants/legacyConfig';
import {
  getFontSizeForBlockKind,
  TEXT_BLOCK_LAYOUT_DEFAULTS,
  TEXT_ELEMENT_DEFAULT_PADDING,
  TEXT_ELEMENT_DEFAULT_LINE_HEIGHT,
  computeBlockLayoutHeight,
  getFontColorForBackground,
  getBlockKindDisplayName,
  type TextBlockKind,
} from '@/components/canvas/elements/text/constants';
import { DEFAULT_LIST_STYLE } from '@/components/canvas/elements/text/model/textStyles';
import { isHeadingBlockKind } from '@/components/canvas/elements/text/utils/textContent';
import { createPageWithSize } from '@/components/canvas/page/createPage';
import { MAX_WORKSPACE_PAGES } from '@/components/canvas/workspace/viewport/workspaceLimits';
import {
  cloneElementDeep,
  cloneElementsForClipboard,
  collectSubtreeElementIds,
  pasteRootElementIds,
  pasteSubtreeRoots,
  shouldApplyPasteTranslation,
} from '@/components/canvas/document/cloneElement';
import { clonePageWithNewIds } from '@/components/canvas/page/clonePage';
import { duplicatePageDisplayName } from '@/components/canvas/page/duplicatePageName';
import {
  resolveLayoutSwitchFitTarget,
  scheduleFitAfterLayoutModeSwitch,
} from '@/components/canvas/workspace/controllers/layoutModeSwitch';
import {
  applySingleStackLayout,
  applyWorkspaceLayoutPatch,
  nextPageWorkspaceLayout,
  normalizePagesWorkspaceLayout,
  relayoutPagesForMode,
  relayoutPagesFromIndex,
  workspaceLayoutAfterPage,
  workspaceLayoutOnSingleStack,
} from '@/components/canvas/workspace/model/pageArrangement';
import {
  layoutCenteredOnAnchor,
  resolveElementInsertContext,
  resolveTextInsertContext,
  textInsertActivationPatch,
  type ElementInsertScope,
  type TextInsertVia,
} from '@/components/canvas/workspace/model/elementInsertTarget';
import type { MarqueeScope } from '@/components/canvas/workspace/controllers/marqueeSelectUtils';
import { isWorkspacePageLayoutMode } from '@/components/canvas/workspace/model/workspacePageLayoutMode';
import type { WorkspacePageLayoutMode } from '@/components/canvas/document/publicTypes';
import type { PageWorkspaceLayout } from '@/components/canvas/document/publicTypes';
import { resolveTableSizePatch } from '@/components/canvas/elements/table/utils/tableSizePatch';
import { distributeTableSizes } from '@/components/canvas/elements/table/utils/tableDrawDefaults';
import {
  boxSpacing,
  createChartElement as createChartCanvasElement,
  createCircleElement as createCircleCanvasElement,
  createTextContentData,
  createFreehandElement as createFreehandCanvasElement,
  createGroupElement,
  createImageElement as createImageCanvasElement,
  createLineElement as createLineCanvasElement,
  createRectElement as createRectCanvasElement,
  createTableElement as createTableCanvasElement,
  createTextElement as createTextCanvasElement,
  createTriangleElement as createTriangleCanvasElement,
  createVectorElement as createVectorCanvasElement,
  solidPaint,
  stroke,
} from '@/components/canvas/elements/registry/factories';
import type { SliceDeps } from '../deps';
import type { CanvasActions, HistorySnapshot, PasteElementsTarget } from '@/components/canvas/store/types';
import { genId, buildDefaultChartSpec } from '../helpers';

function resolveInsert(get: SliceDeps['get']) {
  const s = get();
  return resolveElementInsertContext(
    s.pages,
    s.currentPageId,
    s.panX,
    s.panY,
    s.zoom,
  );
}

function pushElementOnDraft(
  draft: { pages: Page[]; workspaceElements: Element[] },
  ctx: ReturnType<typeof resolveElementInsertContext>,
  el: Element,
) {
  if (ctx.scope === 'page' && ctx.page) {
    const c = draft.pages.find((p) => p.id === ctx.page!.id);
    if (c) {
      c.elements.push(el);
      c.updatedAt = Date.now();
    }
  } else {
    draft.workspaceElements.push(el);
  }
}

function selectInserted(
  id: string,
  scope: ElementInsertScope,
): {
  selectedElementIds: string[];
  isPageSelected: false;
  selectionScope: MarqueeScope | null;
  editingTextId: null;
  editingGroupId: null;
} {
  return {
    selectedElementIds: [id],
    isPageSelected: false,
    selectionScope: scope === 'workspace' ? 'workspace' : 'page',
    editingTextId: null,
    editingGroupId: null,
  };
}

type DocumentSliceReturn = Pick<
  CanvasActions,
  | 'getCurrentPage'
  | 'setPages'
  | 'setCurrentPageId'
  | 'setWorkspacePageLayoutMode'
  | 'addPage'
  | 'addPageAfter'
  | 'duplicatePage'
  | 'setPageName'
  | 'deletePage'
  | 'setPageSize'
  | 'applyPageFrameResizeSilent'
  | 'commitPageFrameResize'
  | 'setPageBackground'
  | 'updatePageWorkspaceLayout'
  | 'updatePageWorkspaceLayoutLive'
  | 'bringPageToFront'
  | 'addElement'
  | 'addImageElement'
  | 'addImageElementAt'
  | 'addTextElement'
  | 'addTextElementAt'
  | 'addParagraphTextAt'
  | 'addTableElementAt'
  | 'addShapeElementAt'
  | 'addLineElement'
  | 'addRectElement'
  | 'addCircleElement'
  | 'addTriangleElement'
  | 'addVectorElement'
  | 'addFreehandElementAt'
  | 'addChartElement'
  | 'setElements'
  | 'updateElement'
  | 'updateElementSilent'
  | 'updateElementMeta'
  | 'updateElementLayout'
  | 'updateElementSize'
  | 'updateElementAppearance'
  | 'updateElementTypography'
  | 'updateElementParagraph'
  | 'updateElementInteraction'
  | 'updateElementAnimations'
  | 'updateElementData'
  | 'setElementPositions'
  | 'setWorkspaceElementPositions'
  | 'commitElementDrag'
  | 'deleteSelectedElements'
  | 'copySelectedElements'
  | 'cutSelectedElements'
  | 'pasteElements'
  | 'pasteElementsAt'
  | 'pasteFromSystemClipboard'
  | 'pasteFromSystemClipboardAt'
  | 'bringToFront'
  | 'sendToBack'
  | 'bringForward'
  | 'sendBackward'
  | 'alignSelectedTop'
  | 'alignSelectedBottom'
  | 'alignSelectedLeft'
  | 'alignSelectedRight'
  | 'alignSelectedCenterH'
  | 'alignSelectedCenterV'
  | 'distributeSpacingH'
  | 'distributeSpacingV'
  | 'groupElements'
  | 'ungroupElement'
  | 'commitGroupResize'
  | 'updateElementSilentInDocument'
  | 'commitMultiSelectionResize'
  | 'setPageGuideSettings'
  | 'setPageGuidesEnabled'
  | 'patchPageGuideGrid'
>;

function patchElement(target: Element, patch: ElementPatch): void {
  if (patch.meta) target.meta = { ...target.meta, ...patch.meta };
  if (patch.layout) target.layout = { ...target.layout, ...patch.layout };
  if (patch.size) target.size = { ...target.size, ...patch.size };
  if (patch.appearance) target.appearance = { ...target.appearance, ...patch.appearance };
  if (patch.typography) target.typography = { ...target.typography, ...patch.typography };
  if (patch.paragraph) target.paragraph = { ...target.paragraph, ...patch.paragraph };
  if (patch.interaction) target.interaction = { ...target.interaction, ...patch.interaction };
  if (patch.animations) target.animations = patch.animations;
  if (patch.data) {
    // 文本提交含完整 document，须整体替换，避免浅合并留下共享的 document 引用。
    const nextData = patch.data as Element['data'];
    target.data =
      'document' in nextData && nextData.document !== undefined
        ? nextData
        : ({ ...target.data, ...patch.data } as Element['data']);
  }
}

function getElementRectPatch(patch: ElementPatch): Partial<Pick<ElementSize, 'width' | 'height'>> {
  return {
    width: patch.size?.width,
    height: patch.size?.height,
  };
}

type StyledTextPayloadConfig = Parameters<typeof createTextContentData>[0] & {
  fontFamily: string;
  fontSize: number;
  fontWeight: string | number;
  fontStyle?: 'normal' | 'italic';
  color: string;
  textAlign?: ParagraphStyle['textAlign'];
  placeholder?: string;
  fitWidth?: boolean;
  fitHeight?: boolean;
  singleLine?: boolean;
  verticalAlign?: ParagraphStyle['verticalAlign'];
  writingMode?: ParagraphStyle['writingMode'];
  letterSpacing?: number;
  lineHeight?: number | string;
};

/** 创建文本元素内容和壳级默认文字样式。 */
function createStyledTextPayload(config: StyledTextPayloadConfig): {
  data: TextContentData;
  typography: TypographyStyle;
  paragraph: ParagraphStyle;
} {
  return {
    data: createTextContentData(config),
    typography: {
      fontFamily: config.fontFamily,
      fontSize: config.fontSize,
      fontWeight: config.fontWeight,
      fontStyle: config.fontStyle ?? 'normal',
      color: config.color,
      letterSpacing: config.letterSpacing,
      lineHeight: config.lineHeight,
    },
    paragraph: {
      textAlign: config.textAlign ?? 'left',
      writingMode: config.writingMode,
      placeholder: config.placeholder,
      list: DEFAULT_LIST_STYLE,
      verticalAlign: config.verticalAlign ?? 'top',
      fitWidth: config.fitWidth,
      fitHeight: config.fitHeight,
      singleLine: config.singleLine,
    },
  };
}

export function createDocumentSlice(deps: SliceDeps): DocumentSliceReturn {
  const { set, get, mutate, mutateSilent, nextZ } = deps;

  return {
    getCurrentPage: () => {
      const { pages, currentPageId } = get();
      return pages.find((c) => c.id === currentPageId) ?? null;
    },

    setPages: (payload) => {
      const current = get().pages;
      const next = typeof payload === 'function' ? payload(current) : payload;
      mutate((draft) => {
        draft.pages = next;
      });
    },

    setCurrentPageId: (id) => {
      set({
        currentPageId: id,
        isPageSelected: false,
        selectedElementIds: [],
        selectedTableColumn: null,
        selectedTableRow: null,
        selectedTableCells: null,
        editingTableCellId: null,

        editingTextId: null,
        editingGroupId: null,
      });
    },

    setWorkspacePageLayoutMode: (mode: WorkspacePageLayoutMode) => {
      if (!isWorkspacePageLayoutMode(mode)) return;
      const { workspacePageLayoutMode: prev, pages, currentPageId, zoom } = get();
      if (prev === mode) return;

      if (pages.length === 0) {
        set({ workspacePageLayoutMode: mode });
        return;
      }

      const fitTarget = resolveLayoutSwitchFitTarget(pages, currentPageId);
      const singleStackTopPageId =
        mode === 'single' ? fitTarget.pageId : undefined;
      mutate((draft) => {
        draft.pages = relayoutPagesForMode(
          draft.pages,
          mode,
          zoom,
          singleStackTopPageId,
        ) as typeof draft.pages;
      });
      set({
        workspacePageLayoutMode: mode,
        ...(fitTarget.shouldActivateFirstPage
          ? { currentPageId: fitTarget.pageId }
          : {}),
      });

      scheduleFitAfterLayoutModeSwitch(
        (w, h, opts) => get().fitToScreen(w, h, opts),
        mode,
      );
    },

    addPage: () => {
      const { currentPageId } = get();
      return get().addPageAfter(currentPageId);
    },

    addPageAfter: (pageId) => {
      const { pages, workspacePageLayoutMode } = get();
      if (pages.length >= MAX_WORKSPACE_PAGES) return false;

      if (pages.length === 0) {
        const width = 1920;
        const height = 1080;
        let applied = false;
        mutate((draft) => {
          const newPage = createPageWithSize(width, height, {
            name: '',
            workspaceLayout: nextPageWorkspaceLayout([]),
          });
          draft.pages.push(newPage);
          draft.currentPageId = newPage.id;
          applied = true;
        });
        if (!applied) return false;
        set({
          selectedElementIds: [],
          isPageSelected: false,
          editingTextId: null,
          editingGroupId: null,
        });
        return true;
      }

      const idx = pages.findIndex((p) => p.id === pageId);
      const anchor = idx >= 0 ? pages[idx]! : pages[pages.length - 1];
      if (!anchor) return false;
      const width =
        typeof anchor.size?.width === 'number' && anchor.size.width > 0 ? anchor.size.width : 1920;
      const height =
        typeof anchor.size?.height === 'number' && anchor.size.height > 0 ? anchor.size.height : 1080;
      let applied = false;
      mutate((draft) => {
        const anchorIdx = draft.pages.findIndex((p) => p.id === anchor.id);
        if (anchorIdx < 0) return;
        const ref = draft.pages[anchorIdx]!;
        const insertAt = anchorIdx + 1;
        const newPage = createPageWithSize(width, height, {
          name: '',
          workspaceLayout:
            workspacePageLayoutMode === 'single'
              ? workspaceLayoutOnSingleStack(draft.pages, insertAt)
              : { x: 0, y: 0, zIndex: insertAt },
        });
        draft.pages.splice(insertAt, 0, newPage);
        if (workspacePageLayoutMode === 'single') {
          applySingleStackLayout(draft.pages);
        } else {
          relayoutPagesFromIndex(
            draft.pages,
            anchorIdx,
            workspacePageLayoutMode,
            get().zoom,
          );
        }
        draft.currentPageId = newPage.id;
        applied = true;
      });
      if (!applied) return false;
      set({
        selectedElementIds: [],
        isPageSelected: false,
        editingTextId: null,
        editingGroupId: null,
      });
      return true;
    },

    duplicatePage: (pageId) => {
      const { pages, workspacePageLayoutMode } = get();
      if (pages.length >= MAX_WORKSPACE_PAGES) return false;
      const idx = pages.findIndex((p) => p.id === pageId);
      if (idx < 0) return false;
      const source = pages[idx]!;
      const newPageId = `page-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      let applied = false;
      mutate((draft) => {
        const srcIdx = draft.pages.findIndex((p) => p.id === pageId);
        if (srcIdx < 0) return;
        const src = draft.pages[srcIdx]!;
        const newPage = clonePageWithNewIds(src, newPageId);
        newPage.name = duplicatePageDisplayName(src);
        const insertAt = srcIdx + 1;
        newPage.workspaceLayout =
          workspacePageLayoutMode === 'single'
            ? workspaceLayoutOnSingleStack(draft.pages, insertAt)
            : { x: 0, y: 0, zIndex: insertAt };
        draft.pages.splice(insertAt, 0, newPage);
        if (workspacePageLayoutMode === 'single') {
          applySingleStackLayout(draft.pages);
        } else {
          relayoutPagesFromIndex(
            draft.pages,
            srcIdx,
            workspacePageLayoutMode,
            get().zoom,
          );
        }
        draft.currentPageId = newPage.id;
        applied = true;
      });
      if (!applied) return false;
      set({
        selectedElementIds: [],
        isPageSelected: false,
        editingTextId: null,
        editingGroupId: null,
      });
      return true;
    },

    setPageName: (pageId, name) => {
      const trimmed = name.trim();
      mutate((draft) => {
        const page = draft.pages.find((p) => p.id === pageId);
        if (!page || page.name === trimmed) return;
        page.name = trimmed;
        page.updatedAt = Date.now();
      });
    },

    deletePage: (id) => {
      mutate((draft) => {
        const deletedIndex = draft.pages.findIndex((c) => c.id === id);
        if (deletedIndex === -1) return;
        draft.pages.splice(deletedIndex, 1);
        if (draft.currentPageId === id) {
          const next =
            draft.pages[deletedIndex] ?? draft.pages[Math.max(0, deletedIndex - 1)];
          draft.currentPageId = next?.id ?? '';
        }
      });
      set({
        selectedElementIds: [],
        isPageSelected: false,
        selectedPageIds: [],
        selectionScope: null,
        editingTextId: null,
        editingGroupId: null,
      });
    },

    // ═══════════════════════════════════════════════════════
    // Page 属性（当前激活页，见 currentPageId）
    // ═══════════════════════════════════════════════════════

    setPageSize: (size) => {
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (c) { c.size = size; c.updatedAt = Date.now(); }
      });
    },

    applyPageFrameResizeSilent: (payload) => {
      mutateSilent((page) => {
        page.size = { width: payload.width, height: payload.height };
        page.workspaceLayout = {
          ...page.workspaceLayout,
          x: payload.workspaceX,
          y: payload.workspaceY,
        };
        for (const el of page.elements) {
          const start = payload.elementStarts.find((s) => s.id === el.id);
          if (start) {
            el.layout.x = start.x + payload.localOffsetX;
            el.layout.y = start.y + payload.localOffsetY;
          }
        }
        page.updatedAt = Date.now();
      });
    },

    commitPageFrameResize: (payload) => {
      mutate((draft) => {
        const page = draft.pages.find((p) => p.id === draft.currentPageId);
        if (!page) return;
        page.size = { width: payload.width, height: payload.height };
        page.workspaceLayout = {
          ...page.workspaceLayout,
          x: payload.workspaceX,
          y: payload.workspaceY,
        };
        for (const el of page.elements) {
          const start = payload.elementStarts.find((s) => s.id === el.id);
          if (start) {
            el.layout.x = start.x + payload.localOffsetX;
            el.layout.y = start.y + payload.localOffsetY;
          }
        }
        page.updatedAt = Date.now();
      });
    },

    setPageBackground: (background) => {
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (c) { c.background = background; c.updatedAt = Date.now(); }
      });
    },

    updatePageWorkspaceLayout: (pageId, patch) => {
      mutate((draft) => {
        applyWorkspaceLayoutPatch(
          draft.pages,
          pageId,
          patch,
          get().workspacePageLayoutMode,
        );
      });
    },

    updatePageWorkspaceLayoutLive: (pageId, patch) => {
      const { pages, workspacePageLayoutMode } = get();
      const next = produce(pages, (draft) => {
        applyWorkspaceLayoutPatch(draft, pageId, patch, workspacePageLayoutMode);
      });
      if (next !== pages) set({ pages: next });
    },

    bringPageToFront: (pageId) => {
      mutate((draft) => {
        let maxZ = 0;
        for (const p of draft.pages) {
          maxZ = Math.max(maxZ, p.workspaceLayout.zIndex ?? 0);
        }
        const page = draft.pages.find((p) => p.id === pageId);
        if (!page) return;
        page.workspaceLayout = { ...page.workspaceLayout, zIndex: maxZ + 1 };
        page.updatedAt = Date.now();
      });
    },

    // ═══════════════════════════════════════════════════════
    // elements
    // ═══════════════════════════════════════════════════════

    addElement: (element) => {
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (c) { c.elements.push(element); c.updatedAt = Date.now(); }
      });
    },

    addImageElement: (src) => {
      const ctx = resolveInsert(get);
      const id = genId('image');
      const defaultSize = 200;
      const { x, y } = layoutCenteredOnAnchor(
        ctx.anchorX,
        ctx.anchorY,
        defaultSize,
        defaultSize,
      );
      const el: ImageElement = createImageCanvasElement({
        id,
        name: '图片',
        x,
        y,
        width: defaultSize,
        height: defaultSize,
        src,
        objectFit: 'cover',
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set(selectInserted(id, ctx.scope));
    },

    addImageElementAt: (payload) => {
      const s = get();
      const insertVia: TextInsertVia =
        payload.via ?? (s.currentPageId ? 'page' : 'workspace');
      const ctx = resolveTextInsertContext(
        s.pages,
        s.currentPageId,
        { x: payload.x, y: payload.y },
        insertVia,
        payload.pageId,
      );
      const defaultSize = 200;
      const id = genId('image');
      const el: ImageElement = createImageCanvasElement({
        id,
        name: payload.name ?? '图片',
        x: payload.x,
        y: payload.y,
        width: defaultSize,
        height: defaultSize,
        src: payload.src,
        objectFit: 'cover',
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set({ ...selectInserted(id, ctx.scope), ...textInsertActivationPatch(ctx) });
    },

    addTableElementAt: (payload) => {
      const s = get();
      const insertVia: TextInsertVia =
        payload.via ?? (s.currentPageId ? 'page' : 'workspace');
      const ctx = resolveTextInsertContext(
        s.pages,
        s.currentPageId,
        { x: payload.x, y: payload.y },
        insertVia,
        payload.pageId,
      );
      const rows = Math.max(1, payload.rows);
      const cols = Math.max(1, payload.cols);
      const w = Math.max(1, payload.width);
      const h = Math.max(1, payload.height);
      const pageW = ctx.page?.size.width ?? 1920;
      const id = genId('table');
      const el: TableElement = createTableCanvasElement({
        id,
        name: '表格',
        x: payload.x,
        y: payload.y,
        width: w,
        height: h,
        rows,
        cols,
        rowHeights: distributeTableSizes(h, rows),
        colWidths: distributeTableSizes(w, cols),
        padding: boxSpacing(0),
        borderPreset: 'full',
        tableStyle: 'default',
        appearance: { strokes: [stroke(TABLE_BORDER_COLOR, TABLE_BORDER_WIDTH, TABLE_BORDER_STYLE)] },
        typography: {
          fontFamily: DEFAULT_DESIGNER_FONT,
          fontSize: TABLE_DEFAULT_FONT_SIZE,
          fontWeight: TABLE_DEFAULT_FONT_WEIGHT,
          color: TABLE_DEFAULT_TEXT_COLOR,
        },
        paragraph: { textAlign: TABLE_DEFAULT_TEXT_ALIGN },
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set({ ...selectInserted(id, ctx.scope), ...textInsertActivationPatch(ctx) });
      return id;
    },

    addTextElement: (blockKind: TextBlockKind = 'paragraph') => {
      const ctx = resolveInsert(get);
      const page = ctx.page;
      const pageBackground = page?.background ?? { type: 'color' as const, value: '#ffffff' };
      const id = genId('text');

      const layout = TEXT_BLOCK_LAYOUT_DEFAULTS[blockKind];
      const pad = TEXT_ELEMENT_DEFAULT_PADDING;
      const fontSize = getFontSizeForBlockKind(blockKind);
      const totalWidth = layout.width + pad * 2;
      const totalHeight = computeBlockLayoutHeight(blockKind, fontSize) + pad * 2;
      const color = getFontColorForBackground(pageBackground);
      const centered = layoutCenteredOnAnchor(ctx.anchorX, ctx.anchorY, totalWidth, totalHeight);

      const el: TextElement = createTextCanvasElement({
        id,
        name: getBlockKindDisplayName(blockKind),
        x: centered.x,
        y: centered.y,
        width: totalWidth,
        height: totalHeight,
        ...createStyledTextPayload({
          content: '',
          blockKind,
          fontFamily: DEFAULT_DESIGNER_FONT,
          fontSize,
          fontWeight: layout.fontWeight,
          fontStyle: 'normal',
          color,
          textAlign: 'left',
          verticalAlign: 'top',
          lineHeight: TEXT_ELEMENT_DEFAULT_LINE_HEIGHT,
        }),
        padding: boxSpacing(pad),
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set(selectInserted(id, ctx.scope));
      return id;
    },

    addTextElementAt: ({ x, y, width, height, blockKind: blockKindArg, via, pageId }) => {
      const s = get();
      const insertVia: TextInsertVia =
        via ?? (s.currentPageId ? 'page' : 'workspace');
      const ctx = resolveTextInsertContext(
        s.pages,
        s.currentPageId,
        { x, y },
        insertVia,
        pageId,
      );
      const page = ctx.page;
      const id = genId('text');
      const blockKind: TextBlockKind = blockKindArg ?? 'paragraph';
      const layout = TEXT_BLOCK_LAYOUT_DEFAULTS[blockKind];
      const fontSize = getFontSizeForBlockKind(blockKind);
      const pad = TEXT_ELEMENT_DEFAULT_PADDING;
      const el: TextElement = createTextCanvasElement({
        id,
        name: getBlockKindDisplayName(blockKind),
        x,
        y,
        width,
        height,
        ...createStyledTextPayload({
          content: '',
          blockKind,
          fontFamily: DEFAULT_DESIGNER_FONT,
          fontSize,
          fontWeight: layout.fontWeight,
          fontStyle: 'normal',
          color: getFontColorForBackground(
            page?.background ?? { type: 'color' as const, value: '#ffffff' },
          ),
          textAlign: 'left',
          verticalAlign: 'top',
          lineHeight: TEXT_ELEMENT_DEFAULT_LINE_HEIGHT,
        }),
        padding: boxSpacing(pad),
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set({ ...selectInserted(id, ctx.scope), ...textInsertActivationPatch(ctx) });
      return id;
    },

    /** 画布单击：插入指定类型文本，默认 h1 标题 */
    addParagraphTextAt: (x, y, opts) => {
      const s = get();
      const ctx = resolveTextInsertContext(
        s.pages,
        s.currentPageId,
        { x, y },
        opts?.via ?? (s.currentPageId ? 'page' : 'workspace'),
        opts?.pageId,
      );
      const page = ctx.page;
      const blockKind: TextBlockKind = opts?.blockKind ?? 'h1';
      const layout = TEXT_BLOCK_LAYOUT_DEFAULTS[blockKind];
      const fontSize = getFontSizeForBlockKind(blockKind);
      const pad = TEXT_ELEMENT_DEFAULT_PADDING;
      const width = layout.width + pad * 2;
      const height = computeBlockLayoutHeight(blockKind, fontSize) + pad * 2;
      const id = genId('text');
      const el: TextElement = createTextCanvasElement({
        id,
        name: getBlockKindDisplayName(blockKind),
        x,
        y,
        width,
        height,
        ...createStyledTextPayload({
          content: '',
          blockKind,
          fontFamily: DEFAULT_DESIGNER_FONT,
          fontSize,
          fontWeight: layout.fontWeight,
          fontStyle: 'normal',
          color: getFontColorForBackground(
            page?.background ?? { type: 'color' as const, value: '#ffffff' },
          ),
          textAlign: 'left',
          verticalAlign: 'top',
          lineHeight: TEXT_ELEMENT_DEFAULT_LINE_HEIGHT,
        }),
        padding: boxSpacing(pad),
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set({ ...selectInserted(id, ctx.scope), ...textInsertActivationPatch(ctx) });
      return id;
    },

    addShapeElementAt: ({ kind, x, y, width, height, endX, endY, via, pageId }) => {
      const s = get();
      const insertVia: TextInsertVia =
        via ?? (s.currentPageId ? 'page' : 'workspace');
      const ctx = resolveTextInsertContext(
        s.pages,
        s.currentPageId,
        { x, y },
        insertVia,
        pageId,
      );
      const id = genId(
        kind === 'line'
          ? 'line'
          : kind === 'rect'
            ? 'rect'
            : kind === 'circle'
              ? 'circle'
              : kind === 'triangle'
                ? 'triangle'
                : 'vector',
      );
      const appearance = {
        fills: [solidPaint('#e5e5e5')],
        strokes: [stroke(kind === 'line' ? '#000000' : '#999', kind === 'line' ? 2 : 1, 'solid')],
        ...(kind === 'rect' ? { radius: { all: 0 } } : {}),
      };

      let el: Element;
      if (kind === 'line') {
        const ex = endX ?? width;
        const ey = endY ?? 0;
        el = createLineCanvasElement({
          id,
          name: '直线',
          x,
          y,
          width: Math.max(1, Math.abs(ex)),
          height: Math.max(1, Math.abs(ey)),
          start: { x: 0, y: 0 },
          end: { x: ex, y: ey },
          appearance,
          zIndex: nextZ(),
        });
      } else if (kind === 'rect') {
        el = createRectCanvasElement({
          id,
          name: '矩形',
          x,
          y,
          width,
          height,
          appearance: appearance as RectangleElement['appearance'],
          zIndex: nextZ(),
        });
      } else if (kind === 'circle') {
        el = createCircleCanvasElement({
          id,
          name: '圆形',
          x,
          y,
          width,
          height,
          appearance,
          zIndex: nextZ(),
        });
      } else if (kind === 'triangle') {
        el = createTriangleCanvasElement({
          id,
          name: '三角形',
          x,
          y,
          width,
          height,
          direction: 'up',
          appearance,
          zIndex: nextZ(),
        });
      } else if (kind === 'polygon') {
        el = createVectorCanvasElement({
          id,
          name: '多边形',
          x,
          y,
          width,
          height,
          appearance,
          zIndex: nextZ(),
          shape: 'polygon',
          polygonSides: 6,
        });
      } else if (kind === 'heart') {
        el = createVectorCanvasElement({
          id,
          name: '心形',
          x,
          y,
          width,
          height,
          appearance,
          zIndex: nextZ(),
          shape: 'heart',
        });
      } else {
        el = createVectorCanvasElement({
          id,
          name: '星形',
          x,
          y,
          width,
          height,
          appearance,
          zIndex: nextZ(),
          shape: 'star',
          starPoints: 5,
          starInnerRatio: 0.45,
        });
      }

      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set({ ...selectInserted(id, ctx.scope), ...textInsertActivationPatch(ctx) });
      return id;
    },

    addLineElement: () => {
      const ctx = resolveInsert(get);
      const id = genId('line');
      const w = 160;
      const h = 4;
      const { x, y } = layoutCenteredOnAnchor(ctx.anchorX, ctx.anchorY, w, h);
      const el: LineElement = createLineCanvasElement({
        id,
        name: '直线',
        x,
        y,
        width: w,
        height: h,
        end: { x: 160, y: 0 },
        appearance: { strokes: [stroke('#000000', 2, 'solid')] },
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set(selectInserted(id, ctx.scope));
    },

    addRectElement: () => {
      const ctx = resolveInsert(get);
      const id = genId('rect');
      const w = 120;
      const h = 80;
      const { x, y } = layoutCenteredOnAnchor(ctx.anchorX, ctx.anchorY, w, h);
      const el: RectangleElement = createRectCanvasElement({
        id,
        name: '矩形',
        x,
        y,
        width: w,
        height: h,
        appearance: {
          fills: [solidPaint('#e5e5e5')],
          strokes: [stroke('#999', 1, 'solid')],
          radius: { all: 0 },
        },
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set(selectInserted(id, ctx.scope));
    },

    addCircleElement: () => {
      const ctx = resolveInsert(get);
      const id = genId('circle');
      const size = 80;
      const { x, y } = layoutCenteredOnAnchor(ctx.anchorX, ctx.anchorY, size, size);
      const el: CircleElement = createCircleCanvasElement({
        id,
        name: '圆形',
        x,
        y,
        width: size,
        height: size,
        appearance: { fills: [solidPaint('#e5e5e5')], strokes: [stroke('#999', 1, 'solid')] },
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set(selectInserted(id, ctx.scope));
    },

    addTriangleElement: () => {
      const ctx = resolveInsert(get);
      const id = genId('triangle');
      const w = 100;
      const h = 88;
      const { x, y } = layoutCenteredOnAnchor(ctx.anchorX, ctx.anchorY, w, h);
      const el: TriangleElement = createTriangleCanvasElement({
        id,
        name: '三角形',
        x,
        y,
        width: w,
        height: h,
        direction: 'up',
        appearance: { fills: [solidPaint('#e5e5e5')], strokes: [stroke('#999', 1, 'solid')] },
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set(selectInserted(id, ctx.scope));
    },

    addVectorElement: (shape, config = {}) => {
      const s = get();
      const hasExplicitPos = config.x !== undefined && config.y !== undefined;
      const insertVia: TextInsertVia =
        config.via ?? (s.currentPageId ? 'page' : 'workspace');
      const ctx = hasExplicitPos
        ? resolveTextInsertContext(
            s.pages,
            s.currentPageId,
            { x: config.x!, y: config.y! },
            insertVia,
            config.pageId,
          )
        : resolveInsert(get);
      const id = genId('vector');
      const size = 100;
      const w = config.width ?? size;
      const h = config.height ?? size;
      const centered = layoutCenteredOnAnchor(ctx.anchorX, ctx.anchorY, w, h);
      const el: VectorElement = createVectorCanvasElement({
        id,
        name: shape === 'heart' ? '心形' : shape === 'polygon' ? '多边形' : shape === 'star' ? '星形' : '路径',
        x: config.x ?? centered.x,
        y: config.y ?? centered.y,
        width: w,
        height: h,
        appearance: config.appearance ?? { fills: [solidPaint('#e5e5e5')], strokes: [stroke('#999', 1, 'solid')] },
        zIndex: nextZ(),
        shape,
        polygonSides: config.data?.polygonSides,
        starPoints: config.data?.starPoints,
        starInnerRatio: config.data?.starInnerRatio,
        pathData: config.data?.pathData,
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set({
        ...selectInserted(id, ctx.scope),
        ...(hasExplicitPos ? textInsertActivationPatch(ctx) : {}),
      });
    },

    addFreehandElementAt: (config) => {
      const s = get();
      const insertVia: TextInsertVia =
        config.via ?? (s.currentPageId ? 'page' : 'workspace');
      const ctx = resolveTextInsertContext(
        s.pages,
        s.currentPageId,
        { x: config.x, y: config.y },
        insertVia,
        config.pageId,
      );
      const id = genId('freehand');
      const el = createFreehandCanvasElement({
        id,
        name: '涂抹笔',
        x: config.x,
        y: config.y,
        width: config.width,
        height: config.height,
        pathData: config.pathData,
        strokeColor: config.strokeColor,
        strokeWidth: config.strokeWidth,
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set({ ...selectInserted(id, ctx.scope), ...textInsertActivationPatch(ctx) });
    },

    addChartElement: (chartType) => {
      const ctx = resolveInsert(get);
      const id = genId('chart');
      const w = 480;
      const h = 320;
      const { x, y } = layoutCenteredOnAnchor(ctx.anchorX, ctx.anchorY, w, h);
      const spec = buildDefaultChartSpec(chartType);
      const el: ChartElement = createChartCanvasElement({
        id,
        name: chartType === 'bar' ? '柱状图' : chartType === 'line' ? '折线图' : '饼图',
        x,
        y,
        width: w,
        height: h,
        chartType,
        spec,
        zIndex: nextZ(),
      });
      mutate((draft) => {
        pushElementOnDraft(draft, ctx, el);
      });
      set(selectInserted(id, ctx.scope));
    },

    setElements: (elements) => {
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (c) { c.elements = elements; c.updatedAt = Date.now(); }
      });
    },

    updateElement: (id, patch) => {
      const applyPatch = (el: Element) => {
        const sizePatch = getElementRectPatch(patch);
        const resolvedPatch =
          el.type === 'table' && (sizePatch.width !== undefined || sizePatch.height !== undefined)
            ? { ...patch, ...resolveTableSizePatch(el as TableElement, patch) }
            : patch;
        patchElement(el, resolvedPatch);
      };
      mutate((draft) => {
        for (const page of draft.pages) {
          const el = page.elements.find((e) => e.id === id);
          if (!el) continue;
          applyPatch(el);
          page.updatedAt = Date.now();
          return;
        }
        const ws = draft.workspaceElements.find((e) => e.id === id);
        if (ws) applyPatch(ws);
      });
    },

    updateElementSilent: (id, patch) => {
      const applyPatch = (el: Element) => {
        const sizePatch = getElementRectPatch(patch);
        const resolvedPatch =
          el.type === 'table' && (sizePatch.width !== undefined || sizePatch.height !== undefined)
            ? { ...patch, ...resolveTableSizePatch(el as TableElement, patch) }
            : patch;
        patchElement(el, resolvedPatch);
      };
      const { pages, currentPageId, workspaceElements } = get();
      const inWorkspace = workspaceElements.some((e) => e.id === id);
      if (inWorkspace) {
        const nextWs = produce(workspaceElements, (draft) => {
          const el = draft.find((e) => e.id === id);
          if (el) applyPatch(el);
        });
        if (nextWs !== workspaceElements) set({ workspaceElements: nextWs });
        return;
      }
      mutateSilent((page) => {
        const el = page.elements.find((e) => e.id === id);
        if (!el) return;
        applyPatch(el);
        page.updatedAt = Date.now();
      });
    },

    updateElementMeta: (id, patch) => get().updateElement(id, { meta: patch }),
    updateElementLayout: (id, patch) => get().updateElement(id, { layout: patch }),
    updateElementSize: (id, patch) => get().updateElement(id, { size: patch }),
    updateElementAppearance: (id, patch) => get().updateElement(id, { appearance: patch }),
    updateElementTypography: (id, patch) => get().updateElement(id, { typography: patch }),
    updateElementParagraph: (id, patch) => get().updateElement(id, { paragraph: patch }),
    updateElementInteraction: (id, patch) => get().updateElement(id, { interaction: patch }),
    updateElementAnimations: (id, animations) => get().updateElement(id, { animations }),
    updateElementData: (id, patch) => get().updateElement(id, { data: patch }),

    setElementPositions: (updates) => {
      if (updates.length === 0) return;
      flushSync(() => {
        mutate((draft) => {
          const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
          if (!c) return;
          updates.forEach((u) => {
            const el = c.elements.find((e) => e.id === u.id);
            if (!el) return;
            if (u.x !== undefined) el.layout.x = u.x;
            if (u.y !== undefined) el.layout.y = u.y;
          });
          c.updatedAt = Date.now();
        });
      });
    },

    setWorkspaceElementPositions: (updates) => {
      if (updates.length === 0) return;
      flushSync(() => {
        mutate((draft) => {
          updates.forEach((u) => {
            const el = draft.workspaceElements.find((e) => e.id === u.id);
            if (!el) return;
            if (u.x !== undefined) el.layout.x = u.x;
            if (u.y !== undefined) el.layout.y = u.y;
          });
        });
      });
    },

    commitElementDrag: (payload) => {
      if (payload.positions.length === 0) return;
      const posMap = new Map(payload.positions.map((p) => [p.id, p]));
      const rootIds = new Set(payload.positions.map((p) => p.id));

      const collectFromPage = (page: Page, ids: Set<string>): Element[] => {
        const out: Element[] = [];
        const seen = new Set<string>();
        const visit = (id: string) => {
          if (seen.has(id)) return;
          const el = page.elements.find((e) => e.id === id);
          if (!el) return;
          seen.add(id);
          out.push(el);
          if (el.type === 'group') {
            (el as GroupElement).data.children?.forEach((cid) => visit(cid));
          }
        };
        ids.forEach((id) => visit(id));
        return out;
      };

      flushSync(() => {
        mutate((draft) => {
          let moved: Element[] = [];

          if (payload.source === 'page') {
            const page = draft.pages.find((p) => p.id === payload.sourcePageId);
            if (!page) return;
            moved = collectFromPage(page, rootIds);
          } else {
            moved = draft.workspaceElements.filter((e) => rootIds.has(e.id) && !e.layout.parentId);
          }

          if (moved.length === 0) return;

          moved.forEach((el) => {
            const p = posMap.get(el.id);
            if (!p) return;
            el.layout.x = p.x;
            el.layout.y = p.y;
          });

          if (payload.target === 'page') {
            const target = draft.pages.find((p) => p.id === payload.targetPageId);
            if (!target) return;

            if (payload.source === 'page') {
              const page = draft.pages.find((p) => p.id === payload.sourcePageId);
              if (!page) return;
              const removeIds = new Set(moved.map((e) => e.id));
              page.elements = page.elements.filter((e) => !removeIds.has(e.id));
              page.updatedAt = Date.now();
            } else {
              const removeIds = new Set(moved.map((e) => e.id));
              draft.workspaceElements = draft.workspaceElements.filter(
                (e) => !removeIds.has(e.id),
              );
            }

            target.elements.push(...moved);
            target.updatedAt = Date.now();
            return;
          }

          if (payload.source === 'page') {
            const page = draft.pages.find((p) => p.id === payload.sourcePageId);
            if (!page) return;
            const removeIds = new Set(moved.map((e) => e.id));
            page.elements = page.elements.filter((e) => !removeIds.has(e.id));
            page.updatedAt = Date.now();
          }

          draft.workspaceElements.push(...moved);
        });
      });
    },

    deleteSelectedElements: () => {
      const { selectedElementIds, selectedPageIds, selectedBlockIds } = get();
      const hasElements = selectedElementIds.length > 0;
      const hasPages = selectedPageIds.length > 0;
      const hasBlocks = selectedBlockIds.length > 0;
      if (!hasElements && !hasPages && !hasBlocks) return;

      if (hasBlocks) {
        get().deleteSelectedBlocks();
      }

      if (hasPages) {
        const deletePageSet = new Set(selectedPageIds);
        mutate((draft) => {
          const cur = draft.currentPageId;
          const before = draft.pages;
          draft.pages = before.filter((p) => !deletePageSet.has(p.id));
          if (cur && deletePageSet.has(cur)) {
            const oldIdx = before.findIndex((p) => p.id === cur);
            draft.currentPageId =
              draft.pages[oldIdx]?.id ??
              draft.pages[Math.max(0, oldIdx - 1)]?.id ??
              draft.pages[0]?.id ??
              '';
          }
        });
      }

      if (hasElements) {
        const toDelete = new Set(selectedElementIds);
        const expandGroups = (list: Element[]) => {
          list.forEach((el) => {
            if (el.type === 'group' && toDelete.has(el.id)) {
              (el as GroupElement).data.children?.forEach((childId) => toDelete.add(childId));
            }
          });
        };
        get().pages.forEach((p) => expandGroups(p.elements));
        expandGroups(get().workspaceElements);
        mutate((draft) => {
          for (const page of draft.pages) {
            const next = page.elements.filter((el) => !toDelete.has(el.id));
            if (next.length !== page.elements.length) {
              page.elements = next;
              page.updatedAt = Date.now();
            }
          }
          draft.workspaceElements = draft.workspaceElements.filter((el) => !toDelete.has(el.id));
        });
      }

      set({
        selectedElementIds: [],
        selectedBlockIds: [],
        isPageSelected: false,
        selectedPageIds: [],
        selectionScope: null,
        editingTextId: null,
        editingGroupId: null,
        selectedTableColumn: null,
        selectedTableRow: null,
        selectedTableCells: null,
        editingTableCellId: null,

      });
    },

    // ═══════════════════════════════════════════════════════
    // clipboard — 内部 + 系统剪贴板双写
    // ═══════════════════════════════════════════════════════

    copySelectedElements: (mode = 'copy') => {
      const { selectedElementIds, pages, currentPageId } = get();
      if (selectedElementIds.length === 0) return false;
      const page = pages.find((c) => c.id === currentPageId);
      if (!page) return false;
      const ids = collectSubtreeElementIds(page.elements, selectedElementIds);
      const list = cloneElementsForClipboard(page.elements, selectedElementIds);
      set({ _copiedElements: list, _clipboardMode: mode, hasClipboard: true });

      // 写入系统剪贴板（fire-and-forget）
      if (navigator.clipboard?.writeText) {
        const json = JSON.stringify({ type: 'page-elements', elements: list });
        navigator.clipboard.writeText(json).catch(() => {});
      }

      if (mode === 'cut') {
        mutate((draft) => {
          const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
          if (c) {
            c.elements = c.elements.filter((el) => !ids.has(el.id));
            c.updatedAt = Date.now();
          }
        });
        set({ selectedElementIds: [] });
      }
      return true;
    },

    cutSelectedElements: () => get().copySelectedElements('cut'),

    pasteElementsAt: (target: PasteElementsTarget) => {
      const { _copiedElements: list } = get();
      if (list.length === 0) return false;
      const sourceIds = new Set(list.map((el) => el.id));
      const pasteRoots = pasteSubtreeRoots(list);
      const minX = Math.min(...pasteRoots.map((el) => el.layout.x));
      const minY = Math.min(...pasteRoots.map((el) => el.layout.y));
      const dx = target.x - minX;
      const dy = target.y - minY;
      const idMap = new Map<string, string>();
      list.forEach((el, i) => idMap.set(el.id, genId(`${el.type}-paste-${i}`)));

      const makeElements = (maxZ: number): Element[] =>
        list.map((el, i) => {
          const cloned = cloneElementDeep(el);
          const translate = shouldApplyPasteTranslation(cloned, sourceIds);
          const next: Element = {
            ...cloned,
            id: idMap.get(el.id)!,
            layout: {
              ...cloned.layout,
              parentId: cloned.layout.parentId ? idMap.get(cloned.layout.parentId) : undefined,
              x: translate ? cloned.layout.x + dx : cloned.layout.x,
              y: translate ? cloned.layout.y + dy : cloned.layout.y,
              zIndex: maxZ + i + 1,
            },
          };
          if (next.type === 'group') {
            next.data = {
              ...next.data,
              children: next.data.children.map((id) => idMap.get(id) ?? id),
            };
          }
          return next;
        });

      let selectedIds: string[] = [];
      mutate((draft) => {
        if (target.scope === 'page') {
          const page = draft.pages.find((p) => p.id === target.pageId);
          if (!page) return;
          const maxZ = page.elements.length === 0 ? 0 : Math.max(...page.elements.map((e) => e.layout.zIndex));
          const newElements = makeElements(maxZ);
          page.elements.push(...newElements);
          page.updatedAt = Date.now();
          draft.currentPageId = target.pageId;
          selectedIds = pasteRootElementIds(newElements);
          return;
        }
        const maxZ = draft.workspaceElements.length === 0
          ? 0
          : Math.max(...draft.workspaceElements.map((e) => e.layout.zIndex));
        const newElements = makeElements(maxZ);
        draft.workspaceElements.push(...newElements);
        selectedIds = pasteRootElementIds(newElements);
      });
      if (selectedIds.length === 0) return false;
      set({
        selectedElementIds: selectedIds,
        isPageSelected: false,
        selectedPageIds: [],
        selectionScope: target.scope,
        editingTextId: null,
        editingGroupId: null,
        selectedTableColumn: null,
        selectedTableRow: null,
        selectedTableCells: null,
        editingTableCellId: null,
      });
      return true;
    },

    pasteElements: (offsetX = 30, offsetY = 30) => {
      const { _copiedElements: list, pages, currentPageId } = get();
      if (list.length === 0) return false;
      const page = pages.find((c) => c.id === currentPageId);
      if (!page) return false;
      const sourceIds = new Set(list.map((el) => el.id));
      const idMap = new Map<string, string>();
      list.forEach((el, i) => idMap.set(el.id, genId(`${el.type}-paste-${i}`)));
      const maxZ = page.elements.length === 0 ? 0 : Math.max(...page.elements.map((e) => e.layout.zIndex));
      const newElements: Element[] = list.map((el, i) => {
        const cloned = cloneElementDeep(el);
        const translate = shouldApplyPasteTranslation(cloned, sourceIds);
        const next: Element = {
          ...cloned,
          id: idMap.get(el.id)!,
          layout: {
            ...cloned.layout,
            parentId: cloned.layout.parentId ? idMap.get(cloned.layout.parentId) : undefined,
            x: translate ? cloned.layout.x + offsetX : cloned.layout.x,
            y: translate ? cloned.layout.y + offsetY : cloned.layout.y,
            zIndex: maxZ + i + 1,
          },
        };
        if (next.type === 'group') {
          next.data = {
            ...next.data,
            children: next.data.children.map((id) => idMap.get(id) ?? id),
          };
        }
        return next;
      });
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (c) {
          c.elements.push(...newElements);
          c.updatedAt = Date.now();
        }
      });
      set({ selectedElementIds: pasteRootElementIds(newElements) });
      return true;
    },

    /**
     * 从系统剪贴板读取 page-elements JSON 粘贴（兼容历史 canvas-elements），
     * 若剪贴板无有效内容则回退至内部剪贴板。
     */
    pasteFromSystemClipboard: async () => {
      if (navigator.clipboard?.readText) {
        try {
          const text = await navigator.clipboard.readText();
          const parsed = JSON.parse(text) as { type?: string; elements?: Element[] };
          const clipType = parsed?.type;
          const isElementsClip =
            (clipType === 'page-elements' || clipType === 'canvas-elements') &&
            Array.isArray(parsed.elements) &&
            parsed.elements.length > 0;
          if (isElementsClip && parsed.elements) {
            set({ _copiedElements: parsed.elements, hasClipboard: true });
            return get().pasteElements();
          }
        } catch {
          // 用户拒绝剪贴板权限或格式不匹配，回退
        }
      }
      return get().pasteElements();
    },

    pasteFromSystemClipboardAt: async (target) => {
      if (navigator.clipboard?.readText) {
        try {
          const text = await navigator.clipboard.readText();
          const parsed = JSON.parse(text) as { type?: string; elements?: Element[] };
          const clipType = parsed?.type;
          const isElementsClip =
            (clipType === 'page-elements' || clipType === 'canvas-elements') &&
            Array.isArray(parsed.elements) &&
            parsed.elements.length > 0;
          if (isElementsClip && parsed.elements) {
            set({ _copiedElements: parsed.elements, hasClipboard: true });
            return get().pasteElementsAt(target);
          }
        } catch {
          // 用户拒绝剪贴板权限或格式不匹配，回退
        }
      }
      return get().pasteElementsAt(target);
    },

    // ═══════════════════════════════════════════════════════
    // z-order
    // ═══════════════════════════════════════════════════════

    bringToFront: (elementIds) => {
      const { selectedElementIds } = get();
      const ids = new Set(elementIds ?? selectedElementIds);
      if (ids.size === 0) return;
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        const sorted = [...c.elements].sort((a, b) => a.layout.zIndex - b.layout.zIndex);
        const newOrder = [...sorted.filter((e) => !ids.has(e.id)), ...sorted.filter((e) => ids.has(e.id))];
        c.elements = newOrder.map((el, i) => ({ ...el, layout: { ...el.layout, zIndex: i } }));
        c.updatedAt = Date.now();
      });
    },

    sendToBack: (elementIds) => {
      const { selectedElementIds } = get();
      const ids = new Set(elementIds ?? selectedElementIds);
      if (ids.size === 0) return;
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        const sorted = [...c.elements].sort((a, b) => a.layout.zIndex - b.layout.zIndex);
        const newOrder = [...sorted.filter((e) => ids.has(e.id)), ...sorted.filter((e) => !ids.has(e.id))];
        c.elements = newOrder.map((el, i) => ({ ...el, layout: { ...el.layout, zIndex: i } }));
        c.updatedAt = Date.now();
      });
    },

    bringForward: (elementIds) => {
      const { selectedElementIds } = get();
      const ids = new Set(elementIds ?? selectedElementIds);
      if (ids.size === 0) return;
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        const arr = [...c.elements].sort((a, b) => a.layout.zIndex - b.layout.zIndex);
        for (let i = arr.length - 2; i >= 0; i--) {
          if (ids.has(arr[i].id) && !ids.has(arr[i + 1].id)) {
            [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
          }
        }
        c.elements = arr.map((el, i) => ({ ...el, layout: { ...el.layout, zIndex: i } }));
        c.updatedAt = Date.now();
      });
    },

    sendBackward: (elementIds) => {
      const { selectedElementIds } = get();
      const ids = new Set(elementIds ?? selectedElementIds);
      if (ids.size === 0) return;
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        const arr = [...c.elements].sort((a, b) => a.layout.zIndex - b.layout.zIndex);
        for (let i = 1; i < arr.length; i++) {
          if (ids.has(arr[i].id) && !ids.has(arr[i - 1].id)) {
            [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
          }
        }
        c.elements = arr.map((el, i) => ({ ...el, layout: { ...el.layout, zIndex: i } }));
        c.updatedAt = Date.now();
      });
    },

    // ═══════════════════════════════════════════════════════
    // align / distribute
    // ═══════════════════════════════════════════════════════

    alignSelectedTop: () => {
      const { selectedElementIds } = get();
      if (!selectedElementIds.length) return;
      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const selected = page.elements.filter((e) => selectedElementIds.includes(e.id));
      const minY = Math.min(...selected.map((e) => e.layout.y));
      const byId = new Map(selected.map((e) => [e.id, minY]));
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        c.elements.forEach((el) => { const y = byId.get(el.id); if (y !== undefined) el.layout.y = y; });
        c.updatedAt = Date.now();
      });
    },

    alignSelectedBottom: () => {
      const { selectedElementIds } = get();
      if (!selectedElementIds.length) return;
      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const selected = page.elements.filter((e) => selectedElementIds.includes(e.id));
      const maxY = Math.max(...selected.map((e) => e.layout.y + e.size.height));
      const byId = new Map(selected.map((e) => [e.id, maxY - e.size.height]));
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        c.elements.forEach((el) => { const y = byId.get(el.id); if (y !== undefined) el.layout.y = y; });
        c.updatedAt = Date.now();
      });
    },

    alignSelectedLeft: () => {
      const { selectedElementIds } = get();
      if (!selectedElementIds.length) return;
      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const selected = page.elements.filter((e) => selectedElementIds.includes(e.id));
      const minX = Math.min(...selected.map((e) => e.layout.x));
      const byId = new Map(selected.map((e) => [e.id, minX]));
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        c.elements.forEach((el) => { const x = byId.get(el.id); if (x !== undefined) el.layout.x = x; });
        c.updatedAt = Date.now();
      });
    },

    alignSelectedRight: () => {
      const { selectedElementIds } = get();
      if (!selectedElementIds.length) return;
      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const selected = page.elements.filter((e) => selectedElementIds.includes(e.id));
      const maxX = Math.max(...selected.map((e) => e.layout.x + e.size.width));
      const byId = new Map(selected.map((e) => [e.id, maxX - e.size.width]));
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        c.elements.forEach((el) => { const x = byId.get(el.id); if (x !== undefined) el.layout.x = x; });
        c.updatedAt = Date.now();
      });
    },

    alignSelectedCenterH: () => {
      const { selectedElementIds } = get();
      if (!selectedElementIds.length) return;
      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const selected = page.elements.filter((e) => selectedElementIds.includes(e.id));
      const centerX = (Math.min(...selected.map((e) => e.layout.x)) + Math.max(...selected.map((e) => e.layout.x + e.size.width))) / 2;
      const byId = new Map(selected.map((e) => [e.id, centerX - e.size.width / 2]));
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        c.elements.forEach((el) => { const x = byId.get(el.id); if (x !== undefined) el.layout.x = x; });
        c.updatedAt = Date.now();
      });
    },

    alignSelectedCenterV: () => {
      const { selectedElementIds } = get();
      if (!selectedElementIds.length) return;
      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const selected = page.elements.filter((e) => selectedElementIds.includes(e.id));
      const centerY = (Math.min(...selected.map((e) => e.layout.y)) + Math.max(...selected.map((e) => e.layout.y + e.size.height))) / 2;
      const byId = new Map(selected.map((e) => [e.id, centerY - e.size.height / 2]));
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        c.elements.forEach((el) => { const y = byId.get(el.id); if (y !== undefined) el.layout.y = y; });
        c.updatedAt = Date.now();
      });
    },

    distributeSpacingH: () => {
      const { selectedElementIds } = get();
      if (selectedElementIds.length < 3) return;
      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const selected = page.elements
        .filter((e) => selectedElementIds.includes(e.id))
        .sort((a, b) => a.layout.x + a.size.width / 2 - (b.layout.x + b.size.width / 2));
      const totalW = selected.reduce((sum, e) => sum + e.size.width, 0);
      const span = (selected[selected.length - 1].layout.x + selected[selected.length - 1].size.width) - selected[0].layout.x;
      const gap = (span - totalW) / (selected.length - 1);
      let x = selected[0].layout.x;
      const byId = new Map(selected.map((e) => { const u = x; x += e.size.width + gap; return [e.id, u]; }));
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        c.elements.forEach((el) => { const nx = byId.get(el.id); if (nx !== undefined) el.layout.x = nx; });
        c.updatedAt = Date.now();
      });
    },

    distributeSpacingV: () => {
      const { selectedElementIds } = get();
      if (selectedElementIds.length < 3) return;
      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const selected = page.elements
        .filter((e) => selectedElementIds.includes(e.id))
        .sort((a, b) => a.layout.y + a.size.height / 2 - (b.layout.y + b.size.height / 2));
      const totalH = selected.reduce((sum, e) => sum + e.size.height, 0);
      const span = (selected[selected.length - 1].layout.y + selected[selected.length - 1].size.height) - selected[0].layout.y;
      const gap = (span - totalH) / (selected.length - 1);
      let y = selected[0].layout.y;
      const byId = new Map(selected.map((e) => { const u = y; y += e.size.height + gap; return [e.id, u]; }));
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        c.elements.forEach((el) => { const ny = byId.get(el.id); if (ny !== undefined) el.layout.y = ny; });
        c.updatedAt = Date.now();
      });
    },

    // ═══════════════════════════════════════════════════════
    // group — 编组 / 解组（修正旋转坐标变换）
    // ═══════════════════════════════════════════════════════

    groupElements: (elementIds) => {
      const { pages, currentPageId } = get();
      const page = pages.find((c) => c.id === currentPageId);
      if (!page || elementIds.length < 2) return;
      const roots = page.elements.filter((el) => !el.layout.parentId && elementIds.includes(el.id));
      if (roots.length < 2) return;

      const minX = Math.min(...roots.map((e) => e.layout.x));
      const minY = Math.min(...roots.map((e) => e.layout.y));
      const maxX = Math.max(...roots.map((e) => e.layout.x + e.size.width));
      const maxY = Math.max(...roots.map((e) => e.layout.y + e.size.height));
      const groupId = genId('group');
      const maxZ = Math.max(...roots.map((e) => e.layout.zIndex), -1);

      const group: GroupElement = createGroupElement({
        id: groupId,
        name: '组',
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        zIndex: maxZ + 1,
        children: elementIds.filter((id) => roots.some((r) => r.id === id)),
      });
      const childIds = new Set(elementIds);

      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        // Convert children to group-relative coords (group has no rotation initially)
        c.elements.forEach((el) => {
          if (childIds.has(el.id)) {
            el.layout.parentId = groupId;
            el.layout.x = el.layout.x - minX;
            el.layout.y = el.layout.y - minY;
          }
        });
        // Remove non-root children that are handled by group, add group element
        const rest = c.elements.filter((el) => !childIds.has(el.id));
        c.elements = [...rest, ...c.elements.filter((el) => childIds.has(el.id)), group];
        c.updatedAt = Date.now();
      });
      set({ selectedElementIds: [groupId] });
    },

    ungroupElement: (groupId) => {
      const { pages, currentPageId } = get();
      const page = pages.find((c) => c.id === currentPageId);
      if (!page) return;
      const group = page.elements.find((el) => el.id === groupId && el.type === 'group') as GroupElement | undefined;
      if (!group) return;

      const childIds = group.data.children ?? [];
      const children = childIds.map((id) => page.elements.find((e) => e.id === id)).filter(Boolean) as Element[];

      // 正确处理旋转：将子元素局部坐标通过组的旋转矩阵变换为世界坐标
      const theta = (group.layout.rotation * Math.PI) / 180;
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);
      const gCx = group.layout.x + group.size.width / 2;
      const gCy = group.layout.y + group.size.height / 2;

      const absChildren: Element[] = children.map((el) => {
        // 子元素中心在 group 局部空间的坐标（以 group 左上角为原点）
        const lCx = el.layout.x + el.size.width / 2;
        const lCy = el.layout.y + el.size.height / 2;
        // 相对于 group 中心的偏移量
        const dx = lCx - group.size.width / 2;
        const dy = lCy - group.size.height / 2;
        // 旋转后的世界中心坐标
        const wCx = gCx + dx * cos - dy * sin;
        const wCy = gCy + dx * sin + dy * cos;
        return {
          ...el,
          layout: {
            ...el.layout,
            parentId: undefined,
            x: wCx - el.size.width / 2,
            y: wCy - el.size.height / 2,
            rotation: el.layout.rotation + group.layout.rotation,
          },
        };
      });

      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        const rest = c.elements.filter((el) => el.id !== groupId && !childIds.includes(el.id));
        c.elements = [...rest, ...absChildren];
        c.updatedAt = Date.now();
      });
      set({ selectedElementIds: childIds });
    },

    updateElementSilentInDocument: (id, patch) => {
      const { pages, workspaceElements } = get();
      const applyPatch = (el: Element) => {
        const sizePatch = getElementRectPatch(patch);
        const resolvedPatch =
          el.type === 'table' && (sizePatch.width !== undefined || sizePatch.height !== undefined)
            ? { ...patch, ...resolveTableSizePatch(el as TableElement, patch) }
            : patch;
        patchElement(el, resolvedPatch);
      };

      let foundInPage = false;
      const nextPages = produce(pages, (draft) => {
        for (const page of draft) {
          const el = page.elements.find((e) => e.id === id);
          if (!el) continue;
          applyPatch(el);
          page.updatedAt = Date.now();
          foundInPage = true;
          return;
        }
      });

      let nextWs = workspaceElements;
      if (!foundInPage) {
        nextWs = produce(workspaceElements, (draft) => {
          const el = draft.find((e) => e.id === id);
          if (el) applyPatch(el);
        });
      }

      if (foundInPage || nextWs !== workspaceElements) {
        set({ pages: nextPages, workspaceElements: nextWs });
      }
    },

    commitMultiSelectionResize: (patches) => {
      const byId = new Map(patches.map((p) => [p.id, p.patch]));
      mutate((draft) => {
        for (const page of draft.pages) {
          let touched = false;
          page.elements.forEach((el) => {
            const p = byId.get(el.id);
            if (p) {
              patchElement(el, p);
              touched = true;
            }
          });
          if (touched) page.updatedAt = Date.now();
        }
        draft.workspaceElements.forEach((el) => {
          const p = byId.get(el.id);
          if (p) patchElement(el, p);
        });
      });
    },

    commitGroupResize: (groupId, groupPatch, childrenPatches) => {
      const byId = new Map(childrenPatches.map((cp) => [cp.id, cp.patch]));
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (!c) return;
        c.elements.forEach((el) => {
          if (el.id === groupId) {
            patchElement(el, {
              layout: { x: groupPatch.x, y: groupPatch.y },
              size: { width: groupPatch.width, height: groupPatch.height },
            });
          } else {
            const patch = byId.get(el.id);
            if (patch) patchElement(el, patch);
          }
        });
        c.updatedAt = Date.now();
      });
    },

    // ═══════════════════════════════════════════════════════
    // 辅助线（栅格系统）
    // ═══════════════════════════════════════════════════════

    setPageGuideSettings: (guideSettings) => {
      mutate((draft) => {
        const c = draft.pages.find((cv) => cv.id === draft.currentPageId);
        if (c) {
          c.guideSettings = guideSettings;
          c.updatedAt = Date.now();
        }
      });
    },

    setPageGuidesEnabled: (enabled) => {
      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const current = resolvePageGuideSettings(page);
      get().setPageGuideSettings({ ...current, enabled });
    },

    patchPageGuideGrid: (patch) => {
      const page = get().pages.find((c) => c.id === get().currentPageId);
      if (!page) return;
      const base = page.guideSettings?.grid ?? DEFAULT_GRID_GUIDE_SETTINGS;
      const next: GridGuideSettings = {
        columns:
          patch.columns !== undefined
            ? clampGridGuideValue(patch.columns, GRID_COLUMNS)
            : base.columns,
        rows:
          patch.rows !== undefined ? clampGridGuideValue(patch.rows, GRID_ROWS) : base.rows,
        gutter:
          patch.gutter !== undefined
            ? clampGridGuideValue(patch.gutter, GRID_GUTTER)
            : base.gutter,
        margin:
          patch.margin !== undefined
            ? clampGridGuideValue(patch.margin, GRID_MARGIN)
            : base.margin,
      };
      get().setPageGuideSettings({
        enabled: page.guideSettings?.enabled ?? false,
        system: page.guideSettings?.system ?? 'grid',
        grid: next,
      });
    },
  };
}
